'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';

const aboutSections = [
  { id: 'about', label: 'About Us', href: '/about' },
  { id: 'history', label: 'History', href: '/about/history' },
  { id: 'mission', label: 'Mission', href: '/about/mission' },
  { id: 'vision', label: 'Vision', href: '/about/vision' },
  { id: 'membership', label: 'Membership', href: '/about/membership' },
  { id: 'quality', label: 'Quality Management', href: '/about/quality-management' },
  { id: 'ethics', label: 'Business Ethics', href: '/about/business-ethics' },
];

export default function AboutPage() {
  return (
    <>
      <Header />
      <main className="min-h-screen" style={{ background: '#F4F6F9', color: '#0F2340' }}>
        {/* Hero */}
        <div className="relative pt-32 pb-16 px-6" style={{ background: 'linear-gradient(180deg, #0F2340 0%, #1A3558 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="max-w-5xl mx-auto">
            <p className="text-xs uppercase tracking-widest font-body mb-3" style={{ color: '#7EC8DC' }}>World Transport Overseas</p>
            <h1 className="text-4xl lg:text-5xl font-bold font-body mb-4" style={{ color: '#FFFFFF' }}>The Company</h1>
            <p className="text-lg font-body max-w-2xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              A leading international logistics company with 32 offices across 16 countries on three continents.
            </p>
          </div>
        </div>
        {/* Sub-nav */}
        <div className="sticky top-20 z-30 px-6 py-3" style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid rgba(15,35,64,0.1)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-5xl mx-auto flex flex-wrap gap-1">
            {aboutSections?.map((s) => (
              <Link
                key={s?.id}
                href={s?.href}
                className={`px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200 ${s?.id === 'about' ? 'text-white' : ''}`}
                style={s?.id === 'about'
                  ? { background: '#1B6B8A', color: '#FFFFFF', border: '1px solid rgba(27,107,138,0.5)' }
                  : { background: 'transparent', color: '#3A5070', border: '1px solid rgba(15,35,64,0.15)' }}
                onMouseEnter={(e) => { if (s?.id !== 'about') { e.currentTarget.style.background = 'rgba(27,107,138,0.1)'; e.currentTarget.style.color = '#0F2340'; } }}
                onMouseLeave={(e) => { if (s?.id !== 'about') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#3A5070'; } }}
              >
                {s?.label}
              </Link>
            ))}
          </div>
        </div>
        {/* Content */}
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold font-body mb-6" style={{ color: '#0F2340' }}>About World Transport Overseas</h2>
              <div className="space-y-5 text-base font-body leading-relaxed" style={{ color: '#3A5070' }}>
                <p>
                  World Transport Overseas (WTO) is a leading international logistics company with 32 offices across 16 countries on three continents. The company provides ocean, air, and road freight services, along with a full range of customized logistics solutions tailored to clients' specific needs.
                </p>
                <p>
                  Through its network of own offices and reliable partners in more than 100 countries, WTO ensures global coverage with local expertise, delivering efficiency, transparency, and full control at every stage of the transport process.
                </p>
                <p>
                  The company's policy is focused on providing reliable, timely, and competitive logistics services, built upon extensive professional experience and an innovative mindset. As part of its long-term strategy, Vision 2030, World Transport Overseas aims to become a truly global logistics company by 2030, investing actively in digitalization, automation, and sustainable development across all operations.
                </p>
                <p>
                  With more than 400 dedicated employees across 16 countries on 3 continents, WTO focuses on delivering outstanding service and memorable customer experiences. WTO is committed to a more sustainable future for the industry, operating responsibly and in harmony with the environment.
                </p>
                <p>
                  WTO Headquarters has been recognized as the Company of the Year in the Services sector by Forbes Business Awards in both 2020 and 2024. This prestigious recognition underscores the commitment to quality and innovation in the freight forwarding industry.
                </p>
              </div>
            </div>

            {/* Stats sidebar */}
            <div className="space-y-4">
              {[
                { value: '32', label: 'Offices Worldwide' },
                { value: '16', label: 'Countries' },
                { value: '3', label: 'Continents' },
                { value: '400+', label: 'Employees' },
                { value: '100+', label: 'Partner Countries' },
                { value: '2001', label: 'Founded' },
              ]?.map((stat) => (
                <div key={stat?.label} className="rounded-2xl p-5 text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(27,107,138,0.2)', boxShadow: '0 2px 8px rgba(15,35,64,0.06)' }}>
                  <p className="text-3xl font-bold font-body" style={{ color: '#1B6B8A' }}>{stat?.value}</p>
                  <p className="text-sm font-body mt-1" style={{ color: '#3A5070' }}>{stat?.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation cards to sub-pages */}
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {aboutSections?.slice(1)?.map((s) => (
              <Link
                key={s?.id}
                href={s?.href}
                className="group rounded-2xl p-6 transition-all duration-300"
                style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)', boxShadow: '0 2px 8px rgba(15,35,64,0.06)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(27,107,138,0.06)'; e.currentTarget.style.borderColor = 'rgba(27,107,138,0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = 'rgba(15,35,64,0.1)'; }}
              >
                <p className="text-base font-semibold font-body mb-2" style={{ color: '#0F2340' }}>{s?.label}</p>
                <div className="flex items-center gap-1 text-xs font-body" style={{ color: '#1B6B8A' }}>
                  Learn more
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
